import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import apiClient, { getTrialStatus } from '../api/client';

// Helper function to calculate test percentage (same logic as Results.js)
const calculateTestPercentage = (test) => {
  if (!test.questions || test.questions.length === 0) {
    // Fallback to old calculation if no questions data
    const pct = Math.round((test.score / test.totalQuestions) * 100);
    return Math.min(pct, 100);
  }

  const hasWritingQuestions = test.questions.some(q => q.type === 'written');
  
  if (hasWritingQuestions && test.questions.every(q => q.type === 'written')) {
    // All writing questions - use average AI score
    const scoredQuestions = test.questions.filter(q => q.aiScore !== undefined);
    if (scoredQuestions.length > 0) {
      const totalAiScore = scoredQuestions.reduce((sum, q) => sum + q.aiScore, 0);
      return Math.min(Math.round(totalAiScore / scoredQuestions.length), 100);
    }
    // No AI scores yet, use traditional calculation
    const pct = Math.round((test.score / test.totalQuestions) * 100);
    return Math.min(pct, 100);
  } else if (hasWritingQuestions) {
    // Mixed questions - calculate weighted average
    let totalScore = 0;
    test.questions.forEach(q => {
      if (q.type === 'written' && q.aiScore !== undefined) {
        totalScore += q.aiScore;
      } else if (q.type !== 'written') {
        totalScore += q.isCorrect ? 100 : 0;
      }
    });
    return Math.min(Math.round(totalScore / test.questions.length), 100);
  }
  
  // No writing questions - traditional calculation
  const pct = Math.round((test.score / test.totalQuestions) * 100);
  return Math.min(pct, 100);
};

// Calculate NSW-style scaled score (out of 30)
const calculateNSWScaledScore = (rawScore, totalQuestions) => {
  if (!totalQuestions || totalQuestions === 0) return 0;
  const scaledScore = (rawScore / totalQuestions) * 30;
  return Math.round(scaledScore * 10) / 10; // Round to 1 decimal place
};

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [userExamType, setUserExamType] = useState('selective');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [trialStatus, setTrialStatus] = useState({
    trialTopicsRemaining: 0,
    trialWritingRemaining: 0,
    hasTrialAccess: false
  });
  const [stats, setStats] = useState({
    totalTests: 0,
    topicTests: 0,
    averageScore: 0,
    totalTimeSpent: 0,
    recentTests: [],
    strongTopics: [],
    weakTopics: [],
    totalQuestions: 0,
    writingActivities: 0, // Track writing studio activities
    currentStreak: 0 // Add streak tracking
  });
  const [loading, setLoading] = useState(true);
  const [topicFilter, setTopicFilter] = useState('All');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedDate, setSelectedDate] = useState(null); // For calendar date filtering

  // Determine if tablet view (769-1024px) or small laptop (1025-1440px)
  const isTablet = windowWidth > 768 && windowWidth <= 1024;
  const isMobile = windowWidth <= 768;
  const isSmallLaptop = windowWidth > 1024 && windowWidth <= 1440;
  const isLargeLaptop = windowWidth > 1440 && windowWidth <= 1920;
  const isDesktop = windowWidth > 1920;
  
  // Smart layout decisions
  const shouldStack = windowWidth <= 1024; // Stack on mobile/tablet
  const shouldShowCompactSidebar = windowWidth > 1024 && windowWidth <= 1366; // Compact on small laptops
  const shouldShowFullLayout = windowWidth > 1366; // Full layout on larger screens
  
  const sidebarWidth = isMobile ? '100%' : (shouldShowCompactSidebar ? '180px' : '240px');
  const rightSidebarWidth = isMobile ? '100%' : (shouldShowCompactSidebar ? '260px' : '300px');

  // Use useLayoutEffect to update width BEFORE paint (synchronously)
  useLayoutEffect(() => {
    const updateWidth = () => {
      setWindowWidth(window.innerWidth);
    };
    
    // Update immediately
    updateWidth();
    
    // Listen for resize events
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [location.pathname]); // Re-run when pathname changes

  const fetchImpersonatedUserData = async (username) => {
    try {
      console.log('Fetching impersonated user data for:', username);
      const response = await apiClient.get(`/users?impersonate=${username}`);
      console.log('Impersonated user data:', response.data);
      
      // Set user data
      setUser(response.data.user);
      setIsPremium(response.data.isPremium);
      setTrialStatus(response.data.trialStatus);
      setStats(response.data.stats);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching impersonated user data:', error);
      alert('Failed to load user data. You may not have admin permissions.');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if impersonating
    const params = new URLSearchParams(location.search);
    const impersonateUsername = params.get('impersonate');
    
    if (impersonateUsername) {
      setIsImpersonating(true);
      setImpersonatedUser(impersonateUsername);
      // Fetch impersonated user's data
      fetchImpersonatedUserData(impersonateUsername);
      return; // Don't fetch regular user data
    }
    
    // Get user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Check premium status
    checkPremiumStatus();

    // Fetch trial status
    try {
      fetchTrialStatus();
    } catch (error) {
      console.error('Error calling fetchTrialStatus:', error);
    }

    // Fetch user statistics from API
    const fetchStats = async () => {
      try {
        console.log('Fetching user stats...');
        const response = await apiClient.get('/user/results');
        console.log('Stats response:', response.data);
        
        // Calculate topic tests vs practice tests
        const recentTests = response.data.recentTests || [];
        const topicTestsCount = recentTests.filter(test => 
          test.category === 'TopicBased'
        ).length;
        
        // Calculate total questions answered
        const totalQuestions = recentTests.reduce((sum, test) => 
          sum + (test.totalQuestions || 0), 0
        );
        
        // Calculate streak
        const streak = calculateStreak(recentTests);
        
        // Fetch writing activities
        let writingActivities = 0;
        try {
          const writingsResponse = await apiClient.get('/writings');
          writingActivities = writingsResponse.data.writings?.length || 0;
          console.log('Writing activities:', writingActivities);
        } catch (writingErr) {
          console.log('Could not fetch writings:', writingErr);
        }
        
        setStats({
          totalTests: response.data.totalTests || 0,
          topicTests: topicTestsCount,
          averageScore: response.data.averageScore || 0,
          totalTimeSpent: response.data.totalTimeSpent || 0,
          recentTests: recentTests,
          strongTopics: [],
          weakTopics: [],
          totalQuestions: totalQuestions,
          writingActivities: writingActivities,
          currentStreak: streak
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
        console.error('Error details:', err.response?.data);
        
        // Check if it's an authentication error
        if (err.response?.status === 401) {
          const errorMessage = err.response?.data?.error || '';
          
          // If user needs verification, the interceptor will handle redirect
          // Just log here for debugging
          console.log('Authentication error - user may need verification');
        }
        // Keep default values - don't show error to user as interceptor handles it
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const calculateStreak = (tests) => {
    if (tests.length === 0) return 0;
    
    // Sort tests by date (most recent first)
    const sortedTests = [...tests].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
    
    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    for (let test of sortedTests) {
      const testDate = new Date(test.date);
      testDate.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.floor((currentDate - testDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === streak) {
        streak++;
      } else if (daysDiff > streak) {
        break;
      }
    }
    
    return streak;
  };

  const checkPremiumStatus = async () => {
    try {
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      const premiumStatus = attributes['custom:premium_status'] === 'true' || attributes['custom:premium_status'] === 'essential';
      setIsPremium(premiumStatus);
      setUserExamType(attributes['custom:exam_type'] || 'selective');
      console.log('Premium status check:', { attributes, premiumStatus });
    } catch (err) {
      console.error('Error checking premium status:', err);
      setIsPremium(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      console.log('Fetching trial status...');
      const status = await getTrialStatus();
      console.log('Trial status received:', status);
      setTrialStatus(status);
    } catch (err) {
      console.error('Error fetching trial status:', err);
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
    const todayDate = today.getDate();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${month + 1}/${day}/${year}`;
      const hasActivity = stats.recentTests.some(test => {
        const testDate = new Date(test.date);
        return testDate.getDate() === day && 
               testDate.getMonth() === month && 
               testDate.getFullYear() === year;
      });
      const isToday = isCurrentMonth && day === todayDate;
      days.push({ day, hasActivity, dateStr, isToday });
    }
    
    return days;
  };

  // Calculate subject-wise progress
  const getSubjectProgress = () => {
    const subjects = {
      'Mathematical Reasoning': { total: 0, correct: 0, color: '#3b82f6' },
      'Reading': { total: 0, correct: 0, color: '#10b981' },
      'Thinking Skills': { total: 0, correct: 0, color: '#f59e0b' },
      ...(userExamType !== 'oc' ? { 'Writing': { total: 0, correct: 0, color: '#ef4444', writingCount: stats.writingActivities } } : {})
    };

    stats.recentTests.forEach(test => {
      if (subjects[test.topic]) {
        subjects[test.topic].total += test.totalQuestions || 0;
        subjects[test.topic].correct += test.correctAnswers || 0;
      }
    });

    return Object.entries(subjects).map(([name, data]) => ({
      name,
      percentage: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      color: data.color,
      total: data.total,
      writingCount: data.writingCount || 0
    }));
  };

  // Filter tests by topic and date
  const filteredTests = stats.recentTests.filter(test => {
    const matchesTopic = topicFilter === 'All' || test.topic === topicFilter;
    
    // Premium users see all tests
    // Trial users see all tests
    // Free users only see Mock Tests
    const matchesTier = isPremium || trialStatus.hasTrialAccess || test.category === 'MockTests';
    
    if (!selectedDate) {
      return matchesTopic && matchesTier;
    }
    
    // Filter by selected date
    const testDate = new Date(test.date);
    const selected = new Date(selectedDate);
    return matchesTopic && matchesTier &&
           testDate.getDate() === selected.getDate() &&
           testDate.getMonth() === selected.getMonth() &&
           testDate.getFullYear() === selected.getFullYear();
  });

  // Calculate filtered stats
  const filteredStats = {
    totalTests: filteredTests.length,
    averageScore: filteredTests.length > 0 
      ? Math.round(filteredTests.reduce((sum, t) => sum + calculateTestPercentage(t), 0) / filteredTests.length)
      : 0,
    totalTimeSpent: filteredTests.reduce((sum, t) => sum + t.timeSpent, 0)
  };

  // Get unique topics for filter buttons
  const topics = ['All', ...new Set(stats.recentTests.map(t => t.topic))].filter(Boolean);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading your dashboard...</p>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();
  const subjectProgress = getSubjectProgress();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: '#f9fafb', 
      flexDirection: shouldStack ? 'column' : 'row',
      position: 'relative'
    }}>
      
      {/* LEFT SIDEBAR - Navigation */}
      <div style={{ 
        width: shouldStack ? '100%' : sidebarWidth,
        background: 'linear-gradient(180deg, #4f46e5 0%, #7c3aed 100%)',
        color: 'white',
        padding: shouldStack ? '15px' : (shouldShowCompactSidebar ? '20px 12px' : '25px 15px'),
        position: 'relative',
        height: 'auto',
        overflowY: 'visible',
        boxShadow: shouldStack ? '0 4px 6px rgba(0,0,0,0.1)' : '4px 0 10px rgba(0,0,0,0.1)',
        zIndex: 10,
        flexShrink: 0
      }}>
        <div style={{ marginBottom: shouldStack ? '15px' : '40px', textAlign: 'center' }}>
          <h2 style={{ fontSize: shouldStack ? '20px' : (shouldShowCompactSidebar ? '22px' : '28px'), fontWeight: 'bold', marginBottom: shouldStack ? '3px' : '5px' }}>PrepSmart</h2>
          <p style={{ fontSize: shouldStack ? '11px' : (shouldShowCompactSidebar ? '11px' : '13px'), opacity: '0.9' }}>Your Success Partner</p>
        </div>

        <nav>
          <div style={{ marginBottom: shouldStack ? '12px' : '30px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', opacity: '0.7', marginBottom: shouldStack ? '8px' : '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              General
            </div>
            <Link to="/dashboard" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                background: 'rgba(255,255,255,0.2)',
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                fontWeight: '600',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}>
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>📊</span>
                <span>Dashboard</span>
              </div>
            </Link>
          </div>

          <div style={{ marginBottom: shouldStack ? '12px' : '30px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', opacity: '0.7', marginBottom: shouldStack ? '8px' : '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Preparation
            </div>
            <Link to="/topic-tests" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                transition: 'all 0.2s',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>📚</span>
                <span>Topic Practice</span>
              </div>
            </Link>
            <Link to="/exams" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                transition: 'all 0.2s',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>🎯</span>
                <span>Mock Exams</span>
              </div>
            </Link>
            {userExamType !== 'oc' && (
            <Link to="/writing-studio" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                transition: 'all 0.2s',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>✍️</span>
                <span>Writing Studio</span>
              </div>
            </Link>
            )}
            <Link to="/analytics" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                transition: 'all 0.2s',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>📈</span>
                <span>Analytics</span>
              </div>
            </Link>
            <Link to="/elite-practice" style={{ textDecoration: 'none' }}>
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                transition: 'all 0.2s',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>💎</span>
                <span>Elite Practice</span>
              </div>
            </Link>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', opacity: '0.7', marginBottom: shouldStack ? '8px' : '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Account
            </div>
            {isPremium && (
              <div style={{ 
                padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                borderRadius: '8px', 
                marginBottom: shouldStack ? '6px' : '8px',
                background: 'rgba(255,215,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                color: 'white',
                border: '1px solid rgba(255,215,0,0.4)',
                fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
              }}>
                <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>⭐</span>
                <span style={{ fontWeight: '600' }}>Premium Member</span>
              </div>
            )}
            {!isPremium && (
              <Link to="/pricing" style={{ textDecoration: 'none' }}>
                <div style={{ 
                  padding: shouldStack ? '8px 12px' : (shouldShowCompactSidebar ? '10px' : '12px 16px'), 
                  borderRadius: '8px', 
                  marginBottom: shouldStack ? '6px' : '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: shouldStack ? '10px' : (shouldShowCompactSidebar ? '8px' : '12px'),
                  color: 'white',
                  transition: 'all 0.2s',
                  fontSize: shouldStack ? '14px' : (shouldShowCompactSidebar ? '13px' : '16px')
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: shouldStack ? '18px' : (shouldShowCompactSidebar ? '16px' : '20px') }}>⭐</span>
                  <span>Upgrade to Premium</span>
                </div>
              </Link>
            )}
          </div>
        </nav>
      </div>

      {/* MAIN CONTENT AREA */}
      <div style={{ 
        marginLeft: '0',
        marginRight: '0',
        flex: 1, 
        padding: shouldStack ? '20px' : (shouldShowCompactSidebar ? '20px' : '30px'),
        width: shouldStack ? '100%' : 'auto',
        minWidth: 0, // Prevent flex item from overflowing
        order: shouldStack ? 2 : 1 // On mobile, show content between sidebars
      }}>
        
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: 'white',
            padding: '15px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>👁️</span>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Admin View Mode - Dashboard Only</div>
                <div style={{ fontSize: '12px', opacity: '0.9' }}>Viewing as: {impersonatedUser} (Navigation disabled in this mode)</div>
              </div>
            </div>
            <button
              onClick={() => window.close()}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              Close Tab
            </button>
          </div>
        )}
        
        {/* Welcome Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
            Welcome back, {user?.name || 'Student'}! 👋
          </h1>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>
            {stats.totalTests === 0 
              ? "Ready to start your journey to selective school success?"
              : "Keep up the great work! Your progress is looking fantastic."
            }
          </p>
        </div>

        {/* Trial Status Banner */}
        {!isPremium && trialStatus.hasTrialAccess && (
          <div style={{ 
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: 'white',
            padding: '20px 25px',
            borderRadius: '12px',
            marginBottom: '25px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
                🎁 Trial Access Active
              </div>
              <div style={{ fontSize: '14px', opacity: '0.95' }}>
                📚 {trialStatus.trialTopicsRemaining} Topic-Based Test{trialStatus.trialTopicsRemaining !== 1 ? 's' : ''} remaining
              </div>
            </div>
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <button style={{
                background: 'white',
                color: '#f59e0b',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                Upgrade to Premium
              </button>
            </Link>
          </div>
        )}

        {/* Trial Exhausted Banner - REMOVED for better UX */}
        {/* Users will see upgrade prompts when trying to access premium features */}

        {/* Getting Started Card for New Users */}
        {stats.totalTests === 0 && (
          <div className="card" style={{ 
            padding: '30px',
            marginBottom: '30px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '16px'
          }}>
            <h2 style={{ fontSize: '24px', marginBottom: '15px', fontWeight: 'bold' }}>🚀 Get Started with PrepSmart</h2>
            <p style={{ fontSize: '16px', marginBottom: '25px', opacity: '0.95', lineHeight: '1.6' }}>
              Welcome to your personalized learning journey! Here's how to make the most of PrepSmart:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>1️⃣</div>
                <h4 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>Take a Practice Test</h4>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Start with a mock exam to assess your current level</p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>2️⃣</div>
                <h4 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>Practice by Topic</h4>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Focus on specific areas that need improvement</p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>3️⃣</div>
                <h4 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>Track Progress</h4>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Monitor your improvement over time</p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>4️⃣</div>
                <h4 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>Achieve Success</h4>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Ace your selective school entrance exam!</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Action Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <Link to="/exams" style={{ textDecoration: 'none' }}>
            <div className="card" style={{ 
              padding: '25px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎯</div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Mock Exams (Free)</h3>
              <p style={{ fontSize: '14px', opacity: '0.9' }}>Full practice tests</p>
            </div>
          </Link>

          <Link to="/topic-tests" style={{ textDecoration: 'none' }}>
            <div className="card" style={{ 
              padding: '25px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📚</div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Topic Practice</h3>
              <p style={{ fontSize: '14px', opacity: '0.9' }}>Master specific topics</p>
            </div>
          </Link>

          {userExamType !== 'oc' && (
          <Link to="/writing-studio" style={{ textDecoration: 'none' }}>
            <div className="card" style={{ 
              padding: '25px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✍️</div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Writing Studio</h3>
              <p style={{ fontSize: '14px', opacity: '0.9' }}>Practice with expert feedback</p>
            </div>
          </Link>
          )}

          <Link to="/analytics" style={{ textDecoration: 'none' }}>
            <div className="card" style={{ 
              padding: '25px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📈</div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Analytics</h3>
              <p style={{ fontSize: '14px', opacity: '0.9' }}>Track your progress</p>
            </div>
          </Link>

          {isPremium ? (
            <Link to="/elite-practice" style={{ textDecoration: 'none' }}>
              <div className="card" style={{ 
                padding: '25px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                background: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
                color: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              >
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>💎</div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Elite Practice</h3>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Exclusive full-length tests</p>
              </div>
            </Link>
          ) : (
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <div className="card" style={{ 
                padding: '25px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              >
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>⭐</div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Go Premium</h3>
                <p style={{ fontSize: '14px', opacity: '0.9' }}>Unlock all features</p>
              </div>
            </Link>
          )}
        </div>

        {/* Recent Activity */}
        {stats.recentTests.length > 0 && (
          <div className="card" style={{ padding: '30px', marginBottom: '30px', borderRadius: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h2 style={{ fontSize: '24px', color: '#1f2937', fontWeight: 'bold', margin: 0 }}>
                  📊 Recent Activity
                </h2>
              </div>
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate(null)}
                  style={{
                    background: '#fbbf24',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Clear Date Filter
                </button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Test</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Subject</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Score</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>NSW Scaled</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentTests.slice(0, 5).map((test, index) => {
                    const percentage = calculateTestPercentage(test);
                    // For NSW scaled score, use percentage (which accounts for AI scores in writing)
                    const nswScaled = calculateNSWScaledScore(percentage, 100);
                    return (
                    <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '15px', fontSize: '14px', color: '#1f2937' }}>{test.name}</td>
                      <td style={{ padding: '15px' }}>
                        <span style={{ 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          fontSize: '12px', 
                          fontWeight: '600',
                          background: '#e0e7ff',
                          color: '#4f46e5'
                        }}>
                          {test.topic}
                        </span>
                      </td>
                      <td style={{ padding: '15px', fontSize: '14px', color: '#6b7280' }}>{test.date}</td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span style={{
                            padding: '6px 14px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            background: percentage >= 70 ? '#d1fae5' : percentage >= 50 ? '#fef3c7' : '#fee2e2',
                            color: percentage >= 70 ? '#065f46' : percentage >= 50 ? '#92400e' : '#991b1b'
                          }}>
                            {percentage}%
                          </span>
                          {/* Only show raw score for non-writing tests */}
                          {test.topic !== 'Writing' && (
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {Math.min(test.score, test.totalQuestions)}/{test.totalQuestions}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center' }}>
                        <span style={{
                          padding: '6px 14px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          background: '#e0e7ff',
                          color: '#4f46e5',
                          border: '2px solid #4f46e5'
                        }}>
                          {nswScaled}/30
                        </span>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <Link to={`/results/${test.id}`}>
                          <button className="btn-secondary" style={{ padding: '8px 16px', fontSize: '14px' }}>
                            View
                          </button>
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT SIDEBAR - Progress & Calendar */}
      <div style={{ 
        width: shouldStack ? '100%' : rightSidebarWidth,
        background: 'white',
        padding: shouldStack ? '20px' : (shouldShowCompactSidebar ? '20px 12px' : '25px 15px'),
        paddingBottom: shouldStack ? '20px' : '30px',
        position: 'relative',
        right: 'auto',
        minHeight: 'auto',
        maxHeight: 'none',
        overflowY: 'visible',
        boxShadow: shouldStack ? '0 -4px 6px rgba(0,0,0,0.05)' : '-4px 0 10px rgba(0,0,0,0.05)',
        borderLeft: shouldStack ? 'none' : '1px solid #e5e7eb',
        borderTop: shouldStack ? '1px solid #e5e7eb' : 'none',
        boxSizing: 'border-box',
        flexShrink: 0,
        order: shouldStack ? 3 : 2 // On mobile, show at bottom
      }}>
        
        {/* Questions Completed Counter */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: shouldShowCompactSidebar ? '18px' : '25px',
          borderRadius: '16px',
          textAlign: 'center',
          marginBottom: shouldShowCompactSidebar ? '18px' : '25px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: shouldShowCompactSidebar ? '36px' : '48px', fontWeight: 'bold', marginBottom: '8px' }}>
            {stats.totalQuestions}
          </div>
          <div style={{ fontSize: shouldShowCompactSidebar ? '13px' : '16px', opacity: '0.95', fontWeight: '600' }}>
            Questions Completed
          </div>
        </div>

        {/* Monthly Calendar */}
        <div style={{ marginBottom: shouldShowCompactSidebar ? '18px' : '25px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            marginBottom: '15px'
          }}>
            <div style={{ 
              fontSize: isSmallLaptop ? '18px' : '20px'
            }}>
              ⏰
            </div>
            <h3 style={{ fontSize: isSmallLaptop ? '16px' : '18px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              {monthNames[currentMonth.getMonth()]} Activity
            </h3>
          </div>
          
          <div style={{ 
            background: '#f9fafb',
            padding: isSmallLaptop ? '12px' : '15px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            {/* Calendar Header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginBottom: '10px' }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} style={{ 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#6b7280' 
                }}>
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar Days */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
              {calendarDays.map((dayObj, i) => (
                <div 
                  key={i} 
                  style={{ 
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    borderRadius: '8px',
                    background: dayObj?.hasActivity ? '#4f46e5' : (dayObj ? '#fff' : 'transparent'),
                    color: dayObj?.hasActivity ? 'white' : (dayObj?.isToday ? '#4f46e5' : '#1f2937'),
                    fontWeight: dayObj?.hasActivity || dayObj?.isToday ? '600' : '400',
                    border: dayObj?.isToday ? '2px solid #4f46e5' : (dayObj && !dayObj.hasActivity ? '1px solid #e5e7eb' : 'none'),
                    cursor: dayObj ? 'pointer' : 'default',
                    position: 'relative',
                    opacity: selectedDate === dayObj?.dateStr ? 1 : (selectedDate && dayObj ? 0.5 : 1),
                    boxShadow: selectedDate === dayObj?.dateStr ? '0 0 0 2px #fbbf24' : 'none'
                  }}
                  onClick={() => {
                    if (dayObj) {
                      if (selectedDate === dayObj.dateStr) {
                        setSelectedDate(null); // Deselect if clicking same date
                      } else {
                        setSelectedDate(dayObj.dateStr);
                      }
                    }
                  }}
                >
                  {dayObj?.day || ''}
                  {dayObj?.isToday && !dayObj?.hasActivity && (
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      background: '#4f46e5'
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subject Progress Circles */}
        <div>
          <h3 style={{ fontSize: isSmallLaptop ? '16px' : '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '15px' }}>
            🎯 Your Progress
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isSmallLaptop ? '12px' : '15px' }}>
            {subjectProgress.map((subject, index) => (
              <div key={index} style={{ 
                background: '#f9fafb',
                padding: isSmallLaptop ? '15px' : '20px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  width: isSmallLaptop ? '70px' : '80px',
                  height: isSmallLaptop ? '70px' : '80px',
                  margin: '0 auto 12px',
                  borderRadius: '50%',
                  background: `conic-gradient(${subject.color} ${subject.percentage * 3.6}deg, #e5e7eb 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <div style={{ 
                    width: isSmallLaptop ? '52px' : '60px',
                    height: isSmallLaptop ? '52px' : '60px',
                    borderRadius: '50%',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isSmallLaptop ? '16px' : '18px',
                    fontWeight: 'bold',
                    color: subject.color
                  }}>
                    {subject.percentage}%
                  </div>
                </div>
                <div style={{ fontSize: isSmallLaptop ? '12px' : '13px', fontWeight: '600', color: '#1f2937', lineHeight: '1.3' }}>
                  {subject.name.replace(' Reasoning', '').replace(' Skills', '')}
                </div>
                {subject.total > 0 && (
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    {subject.total} questions
                  </div>
                )}
                {subject.writingCount > 0 && (
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    ✍️ {subject.writingCount} {subject.writingCount === 1 ? 'writing' : 'writings'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Study Streak */}
        <div style={{ 
          marginTop: isSmallLaptop ? '20px' : '25px',
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          padding: isSmallLaptop ? '10px' : '12px',
          borderRadius: '12px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: isSmallLaptop ? '20px' : '24px', marginBottom: '4px' }}>🔥</div>
          <div style={{ fontSize: isSmallLaptop ? '18px' : '20px', fontWeight: 'bold', marginBottom: '2px' }}>
            {stats.currentStreak}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '600', lineHeight: '1' }}>
            Day Streak
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;